import classNames from "classnames";
import { useContext, useEffect, useMemo, useState } from "react";
import { ObjectSchema, mixed, number, object } from "yup";
import { AvisConstants } from "../../../data/avisConstants";
import { requiredString } from "../../../data/yupConstants";
import {
  ItemViewModel,
  PublicRightsHolderInfo,
  PublicRightsHolderListFilter,
} from "../../../generated/data-contracts";
import { FilterType } from "../../../models";
import { IAxiosError, TableUtils, api } from "../../../utils";
import { AvisForm, AvisLoader } from "../../shared/components";
import {
  AvisDataRenewDate,
  AvisDescriptiveText,
} from "../../shared/components/form";
import {
  AvisFilterableTable,
  IColumnConfig,
} from "../../shared/components/table";
import { AvisContext } from "../../shared/context/AvisContext";
import { PublicRightsHoldersFilterForm } from "../components/filters/PublicRightsHoldersFilterForm";

export function PublicRightsHoldersView() {
  const { res } = useContext(AvisContext);

  const [creationClasses, setCreationClasses] = useState<ItemViewModel[]>();
  const [isCreationClassesLoading, setIsCreationClassesLoading] =
    useState<boolean>(true);
  const [error, setError] = useState<IAxiosError>();

  const getCreationClasses = useMemo(() => {
    if (creationClasses) return;

    setIsCreationClassesLoading(true);

    api
      .publicGetPublicCreationClassesForRightsHolders()
      .then((res) => {
        setCreationClasses(res.data);
      })
      .catch(setError)
      .finally(() => setIsCreationClassesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creationClasses]);
  useEffect(() => {
    getCreationClasses;
  }, [getCreationClasses]);

  const defaultFilterValues: PublicRightsHolderListFilter = useMemo(() => {
    return {
      rightsHolderNameFilterType: "Contains",
      rightsHolderName: "",
      pageIndex: 0,
      pageSize: AvisConstants.default_table_page_size,
    };
  }, []);

  const validationSchema: ObjectSchema<PublicRightsHolderListFilter> = object({
    creationClassId: number(),
    rightsHolderName: requiredString(res),
    rightsHolderNameFilterType: mixed<FilterType>(),
    pageIndex: number().min(0),
    pageSize: number().min(1),
  });

  const columnMetadata: IColumnConfig[] = useMemo(() => {
    return [
      TableUtils.columnConfigStringHTMLEnabled<PublicRightsHolderInfo>(
        "name",
        res("public_RightsHolders_label_name"),
        (
          rowData: PublicRightsHolderInfo,
          value: string | JSX.Element | JSX.Element[],
        ): string | JSX.Element | JSX.Element[] => {
          return (
            <>
              {value}
              {rowData.isDeceased && <sup> *</sup>}
            </>
          );
        },
      ),
      TableUtils.columnConfigStringHTMLEnabled<PublicRightsHolderInfo>(
        "ipiNameNumber",
        res("public_RightsHolders_label_ipiNameNumber"),
      ),
      TableUtils.columnConfigStringHTMLEnabled<PublicRightsHolderInfo>(
        "workTypes",
        res("public_RightsHolders_label_workTypes"),
      ),
      TableUtils.columnConfigStringHTMLEnabled<PublicRightsHolderInfo>(
        "notes",
        res("public_RightsHolders_label_notes"),
      ),
    ];
  }, [res]);
  const renderContent = useMemo(() => {
    return (
      <>
        <AvisDescriptiveText textKey={"portal_PUBL_RightHolderNames"} />
        <AvisDataRenewDate dataType="publicRightsHoldersRegisterUpdateDate" />
        <AvisFilterableTable<
          PublicRightsHolderInfo,
          PublicRightsHolderListFilter
        >
          defaultFilterValues={defaultFilterValues}
          filterValidationSchema={validationSchema}
          setError={setError}
          getApiData={(filter) =>
            api.publicGetPublicRightsHolders(filter as any)
          }
          loadDataOnInit={false}
          renderFilter={(bag, loading, filtered) => (
            <PublicRightsHoldersFilterForm
              bag={bag}
              creationClasses={creationClasses}
              loading={loading || isCreationClassesLoading}
              filtered={filtered}
            />
          )}
          tableColumns={columnMetadata}
          renderFooter={(hasData) => {
            return hasData ? (
              <div className="public-rights-holders-asterix-description">
                {res("public_RightsHolders_label_DeceasedAsterixMeaning")}
              </div>
            ) : (
              <></>
            );
          }}
        />
      </>
    );
  }, [
    res,
    defaultFilterValues,
    validationSchema,
    columnMetadata,
    creationClasses,
    isCreationClassesLoading,
  ]);

  const classNameForm = classNames(
    "avis-public-container",
    "public-rights-holders",
  );

  if (isCreationClassesLoading) return <AvisLoader />;

  return (
    <AvisForm
      title={res("public_RightsHolders_Title")}
      error={error}
      className={classNameForm}
    >
      {renderContent}
    </AvisForm>
  );
}
